/**
 * CodeSearchResult component
 * Displays code search results from ADO repositories
 */
import { FileCode, ExternalLink, Loader2 } from 'lucide-react'

export interface CodeSearchResultItem {
  /** Repository name */
  repository: string
  /** File path within the repository */
  filePath: string
  /** Line number of the match */
  lineNumber: number
  /** Code content snippet */
  content: string
  /** Matched strings to highlight */
  matches: string[]
  /** URL to the file in ADO */
  url?: string
  /** Programming language hint */
  language?: string
}

export interface CodeSearchResultProps {
  /** Search results to display */
  results: CodeSearchResultItem[]
  /** Whether results are loading */
  isLoading?: boolean
}

export function CodeSearchResult({
  results,
  isLoading = false,
}: CodeSearchResultProps) {
  if (isLoading) {
    return (
      <div
        data-testid="code-search-loading"
        className="flex items-center gap-2 text-muted-foreground py-4"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Searching...</span>
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="text-muted-foreground text-sm py-4">
        No results found
      </div>
    )
  }

  const highlightMatches = (content: string, matches: string[]) => {
    if (!matches.length) return content

    let result = content
    const elements: (string | JSX.Element)[] = []
    let lastIndex = 0

    // Find all match positions
    const matchPositions: { start: number; end: number; match: string }[] = []
    for (const match of matches) {
      const index = content.indexOf(match)
      if (index !== -1) {
        matchPositions.push({ start: index, end: index + match.length, match })
      }
    }

    // Sort by position
    matchPositions.sort((a, b) => a.start - b.start)

    // Build highlighted content
    for (const pos of matchPositions) {
      if (pos.start > lastIndex) {
        elements.push(result.substring(lastIndex, pos.start))
      }
      elements.push(
        <mark key={pos.start} data-highlight="true" className="bg-yellow-200 dark:bg-yellow-900 px-0.5 rounded">
          {pos.match}
        </mark>
      )
      lastIndex = pos.end
    }
    if (lastIndex < result.length) {
      elements.push(result.substring(lastIndex))
    }

    return elements.length > 0 ? elements : content
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-muted-foreground">
        {results.length} result{results.length !== 1 ? 's' : ''}
      </div>
      <ul role="list" className="space-y-3">
        {results.map((result, index) => (
          <li
            key={`${result.filePath}-${result.lineNumber}-${index}`}
            role="listitem"
            className="rounded-md border bg-muted/30 p-3"
          >
            {/* File header */}
            <div className="flex items-center gap-2 mb-2">
              <FileCode className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">{result.repository}</span>
                  {result.language && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {result.language}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  {result.url ? (
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline truncate"
                    >
                      {result.filePath}
                    </a>
                  ) : (
                    <span className="text-sm font-medium truncate">{result.filePath}</span>
                  )}
                  <span className="text-sm text-muted-foreground">:{result.lineNumber}</span>
                  {result.url && (
                    <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
              </div>
            </div>
            {/* Code snippet */}
            <div data-code="true" className="overflow-x-auto">
              <code className="block text-xs font-mono bg-muted px-2 py-1.5 rounded whitespace-pre">
                {highlightMatches(result.content, result.matches)}
              </code>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
